import { giftTermLabel } from './referral';

/**
 * THE INVITE-MODAL COPY, as pure data — framework-free so BOTH duplicated modals
 * (town-square + client) build the identical headline from one place, and the give/get
 * wording is provable in `shared/test/` where the React components can't be (the apps have
 * no client-side test runner). The modal keeps only the wiring.
 */

/** The subset of referral settings the copy depends on (a `ReferralSettingsState` satisfies it). */
export interface InviteCopyInput {
  referralPoints: number;
  grantsPoints: boolean;
  grantsSubscription: boolean;
  giftTermMonths: number;
}

export interface InviteCopy {
  /** Bold first line — leads with the gift the friend receives. */
  headlineMain: string;
  /** Emphasised second line — what the referrer earns. */
  headlineSub: string;
  howFriend: string;
  howYou: string;
}

/**
 * The copy follows the live mechanism (admin-switchable). Every friend who joins through the
 * share link gets the welcome points, and the referrer earns theirs when that friend uploads
 * their first artwork. The Human-Made subscription is the emailed invite's special gift, limited
 * by the referrer's allowance. Leads with the gift either way.
 */
export function inviteCopy(settings: InviteCopyInput): InviteCopy {
  const { referralPoints: points, grantsPoints, grantsSubscription, giftTermMonths } = settings;
  const term = giftTermLabel(giftTermMonths);
  const pts = points.toLocaleString();
  const howYou = `You get ${points} points when they upload their first artwork.`;
  const linkGift = `Everyone who joins with your link gets ${points} points — a gift from you.`;

  if (grantsPoints && grantsSubscription) {
    return {
      headlineMain: `Gift a friend with ${pts} Points + ${term} of Human-Made Art Verification`,
      headlineSub: `You also get ${pts}!`,
      howFriend: `${linkGift} Email them an invite and they get ${points} points plus ${term} of Human Made Art verification.`,
      howYou,
    };
  }

  if (grantsSubscription) {
    return {
      headlineMain: `Gift a friend ${term} of Human-Made Art Verification`,
      headlineSub: `You get ${pts} Points!`,
      howFriend: `${linkGift} Email them an invite and they get ${term} of Human Made Art verification.`,
      howYou,
    };
  }

  return {
    headlineMain: `Gift a friend with ${pts} Points`,
    headlineSub: `You also get ${pts}!`,
    howFriend: linkGift,
    howYou,
  };
}

/**
 * A subscription-gifting mechanism where THIS viewer has spent their whole allowance falls
 * back to points server-side, so the invite copy must too. A null allowance is never
 * exhausted — it means the query is still loading, or the mechanism is points-only and skips
 * the query entirely.
 */
export function subscriptionAllowanceExhausted(
  settings: Pick<InviteCopyInput, 'grantsSubscription'>,
  allowance: { remaining: number } | null | undefined
): boolean {
  return Boolean(settings.grantsSubscription && allowance != null && allowance.remaining <= 0);
}

/**
 * The copy to show a SPECIFIC viewer: identical to {@link inviteCopy}, except an artist who
 * has spent every Human-Made gift sees the points-only headline (they can no longer gift a
 * subscription, so promoting one would over-promise).
 */
export function inviteCopyForViewer(
  settings: InviteCopyInput,
  allowance: { remaining: number } | null | undefined
): InviteCopy {
  const exhausted = subscriptionAllowanceExhausted(settings, allowance);
  return inviteCopy(exhausted ? { ...settings, grantsSubscription: false } : settings);
}
